class Core7Benefits extends HTMLElement {
  connectedCallback() {
    this.pills = this.querySelectorAll('[data-benefit-pill]');
    this.panels = this.querySelectorAll('[data-benefit-panel]');
    this.select = this.querySelector('[data-benefit-select]');
    this.activeIndex = 0;

    this.pills.forEach((pill, index) => {
      pill.addEventListener('click', () => this.activate(index));
    });

    if (this.select) {
      this.select.addEventListener('change', () => {
        this.activate(Number(this.select.value));
      });
    }

    // Liquid cannot reliably sort number metaobject fields, so reorder each
    // category track by data-sort-order before wiring carousel controls.
    this.sortIngredientTracks();

    this.panels.forEach((panel) => {
      this.setupIngredientCarousel(panel);
    });

    this.activate(0);
    this.setupImagePreload();
  }

  sortIngredientTracks() {
    this.querySelectorAll('[data-ingredient-track]').forEach((track) => {
      const slides = Array.from(track.querySelectorAll('[data-ingredient-slide]'));
      slides.sort((a, b) => {
        const aOrder = Number(a.dataset.sortOrder);
        const bOrder = Number(b.dataset.sortOrder);
        const aValue = Number.isFinite(aOrder) ? aOrder : Number.POSITIVE_INFINITY;
        const bValue = Number.isFinite(bOrder) ? bOrder : Number.POSITIVE_INFINITY;
        return aValue - bValue;
      });
      slides.forEach((slide) => track.appendChild(slide));
    });
  }

  // Warm the browser cache for every ingredient image (across all category
  // tabs, including hidden panels and off-screen carousel tiles) once the
  // section nears the viewport. The images themselves stay loading="lazy";
  // we just prefetch the correct responsive candidate into cache so that
  // switching tabs or scrolling the carousel reveals them instantly instead
  // of waiting on a first-time download.
  setupImagePreload() {
    const images = Array.from(this.querySelectorAll('.core7-benefits__ingredient-image'));
    if (!images.length) return;

    const warmImages = () => {
      images.forEach((img) => {
        if (img.dataset.preloaded) return;
        img.dataset.preloaded = 'true';

        const preload = new Image();
        // Set sizes before srcset so the browser resolves the right candidate.
        if (img.sizes) preload.sizes = img.sizes;
        if (img.srcset) preload.srcset = img.srcset;
        if (img.src) preload.src = img.src;
      });
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries, obs) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            warmImages();
            obs.disconnect();
          }
        },
        // Start warming before the section is actually on screen.
        { rootMargin: '600px 0px' }
      );
      observer.observe(this);
    } else {
      warmImages();
    }
  }

  setupIngredientCarousel(panel) {
    const track = panel.querySelector('[data-ingredient-track]');
    const prevButton = panel.querySelector('[data-ingredient-prev]');
    const nextButton = panel.querySelector('[data-ingredient-next]');

    if (!track || !prevButton || !nextButton) return;

    prevButton.addEventListener('click', () => {
      panel.classList.remove('has-revealed-end');
      this.scrollIngredientTrack(track, -1);
    });

    nextButton.addEventListener('click', () => {
      this.scrollIngredientTrack(track, 1);
    });

    track.addEventListener(
      'scroll',
      () => {
        this.updateIngredientEndState(panel, track);
        this.updateIngredientArrows(track, prevButton, nextButton);
      },
      { passive: true }
    );

    // Keep arrow availability in sync when the viewport (and so the scrollable
    // width) changes.
    window.addEventListener('resize', () => {
      this.updateIngredientArrows(track, prevButton, nextButton);
    });

    // Initial state.
    this.updateIngredientArrows(track, prevButton, nextButton);
  }

  // Disable the prev arrow when the cards are scrolled fully to the start and
  // the next arrow when fully to the end; enable both when cards sit on either
  // side. If nothing overflows, both are disabled.
  updateIngredientArrows(track, prevButton, nextButton) {
    const maxScroll = track.scrollWidth - track.clientWidth;

    if (maxScroll <= 2) {
      prevButton.disabled = true;
      nextButton.disabled = true;
      return;
    }

    prevButton.disabled = track.scrollLeft <= 2;
    nextButton.disabled = track.scrollLeft >= maxScroll - 2;
  }

  scrollIngredientTrack(track, direction) {
    const slides = Array.from(track.querySelectorAll('[data-ingredient-slide]'));
    if (slides.length <= 1) return false;

    const paddingLeft = Number.parseFloat(getComputedStyle(track).paddingLeft) || 0;
    const scrollPosition = track.scrollLeft + paddingLeft;

    let currentIndex = 0;
    slides.forEach((slide, index) => {
      if (slide.offsetLeft <= scrollPosition + 4) {
        currentIndex = index;
      }
    });

    const targetIndex = Math.max(0, Math.min(slides.length - 1, currentIndex + direction));
    const scrollTarget = Math.max(0, slides[targetIndex].offsetLeft - paddingLeft);

    track.scrollTo({
      left: scrollTarget,
      behavior: 'smooth',
    });

    return targetIndex === slides.length - 1;
  }

  updateIngredientEndState(panel, track) {
    const maxScroll = track.scrollWidth - track.clientWidth;
    if (maxScroll <= 2) {
      panel.classList.remove('has-revealed-end');
      return;
    }

    panel.classList.toggle('has-revealed-end', track.scrollLeft >= maxScroll - 2);
  }

  activate(index) {
    if (index < 0 || index >= this.panels.length) return;

    this.activeIndex = index;

    this.panels.forEach((panel, panelIndex) => {
      const isActive = panelIndex === index;
      panel.hidden = !isActive;
      panel.classList.toggle('is-active', isActive);
      panel.classList.remove('has-revealed-end');

      if (isActive) {
        const track = panel.querySelector('[data-ingredient-track]');
        if (track) {
          track.scrollLeft = 0;
          const prevButton = panel.querySelector('[data-ingredient-prev]');
          const nextButton = panel.querySelector('[data-ingredient-next]');
          if (prevButton && nextButton) {
            this.updateIngredientArrows(track, prevButton, nextButton);
          }
        }
      }
    });

    this.pills.forEach((pill, pillIndex) => {
      const isActive = pillIndex === index;
      pill.classList.toggle('is-active', isActive);
      pill.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    if (this.select && this.select.value !== String(index)) {
      this.select.value = String(index);
    }
  }
}

if (!customElements.get('core7-benefits')) {
  customElements.define('core7-benefits', Core7Benefits);
}
